<script setup lang="ts">
import { ref, onMounted, inject, Ref, computed } from 'vue'
import { useWidgets } from '@/services/WidgetService'
import { Widget } from '@/types/Widgets'
import { Member } from '@/types/Member'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { EllipsisVerticalIcon, PlusCircleIcon } from '@heroicons/vue/24/solid'
import { LayoutGrid, Code2, Eye } from 'lucide-vue-next'
import { toast } from 'vue-sonner'

const { widgets, categories, loading, error, getWidgets, getWidgetCategories, createWidget, deleteWidget, getEmbedCode } = useWidgets()
const vhostId = inject<Ref<string> | null>('vhostId', null)
const userInfo = inject<Ref<Member> | null>('userInfo', null)

const selectedTab = ref('all')
const searchQuery = ref('')
const showCreateDialog = ref(false)
const showEmbedDialog = ref(false)
const embedCode = ref('')
const newWidget = ref({
	name: '',
	type: 'gallery' as string,
	description: '',
})

const filteredWidgets = computed(() => {
	let result = widgets.value

	if (selectedTab.value !== 'all') {
		result = result.filter((w) => w.type === selectedTab.value)
	}

	if (searchQuery.value) {
		const query = searchQuery.value.toLowerCase()
		result = result.filter((w) =>
			w.name.toLowerCase().includes(query) ||
			(w.description && w.description.toLowerCase().includes(query))
		)
	}

	return result
})

const handleCreate = async () => {
	if (!vhostId || !vhostId.value || !newWidget.value.name) {
		return
	}

	const result = await createWidget(vhostId.value, newWidget.value)
	if (result) {
		toast('Widget Created', { description: `"${newWidget.value.name}" has been created.` })
		newWidget.value = { name: '', type: 'gallery', description: '' }
		showCreateDialog.value = false
		await getWidgets(vhostId.value)
	} else {
		toast('Error', { description: 'Failed to create widget.' })
	}
}

const handleDelete = async (widget: Widget) => {
	const success = await deleteWidget(widget.id)
	if (success && vhostId && vhostId.value) {
		toast('Widget Deleted', { description: `"${widget.name}" has been removed.` })
		await getWidgets(vhostId.value)
	}
}

const showEmbed = async (widget: Widget) => {
	const code = await getEmbedCode(widget.id)
	embedCode.value = code || widget.embedCode || `<iframe src="/widget/${widget.id}" width="100%" height="500"></iframe>`
	showEmbedDialog.value = true
}

const copyEmbed = () => {
	navigator.clipboard.writeText(embedCode.value)
	toast('Copied', { description: 'Embed code copied to clipboard.' })
}

onMounted(async () => {
	if (vhostId && vhostId.value) {
		await Promise.all([
			getWidgets(vhostId.value),
			getWidgetCategories(),
		])
	}
})
</script>

<template>
	<div class="p-4 space-y-4">
		<div class="grid gap-4 md:grid-cols-3">
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Total Widgets</CardTitle>
					<LayoutGrid class="h-5 w-5 text-muted-foreground" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ widgets.length }}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Active</CardTitle>
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ widgets.filter((w) => w.status === 'active').length }}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Widget Types</CardTitle>
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ categories.length }}</div>
				</CardContent>
			</Card>
		</div>

		<Card>
			<CardHeader>
				<div class="flex justify-between items-center">
					<div>
						<CardTitle class="text-lg font-normal">Widget Catalog</CardTitle>
						<CardDescription>Create and manage embeddable content widgets</CardDescription>
					</div>
					<Dialog v-model:open="showCreateDialog">
						<DialogTrigger as-child>
							<Button>
								<PlusCircleIcon class="h-4 w-4 mr-2" />
								New Widget
							</Button>
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>Create Widget</DialogTitle>
								<DialogDescription>Create a new embeddable content widget</DialogDescription>
							</DialogHeader>
							<div class="space-y-4 py-4">
								<div class="space-y-2">
									<Label for="widgetName">Name</Label>
									<Input id="widgetName" v-model="newWidget.name" placeholder="Widget name" />
								</div>
								<div class="space-y-2">
									<Label for="widgetType">Type</Label>
									<Select v-model="newWidget.type">
										<SelectTrigger>
											<SelectValue placeholder="Select type" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div class="space-y-2">
									<Label for="widgetDesc">Description</Label>
									<Textarea id="widgetDesc" v-model="newWidget.description" placeholder="Optional description" />
								</div>
							</div>
							<DialogFooter>
								<Button variant="outline" @click="showCreateDialog = false">Cancel</Button>
								<Button @click="handleCreate" :disabled="!newWidget.name">Create</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
				</div>
			</CardHeader>
			<CardContent>
				<div class="space-y-4">
					<div class="flex justify-between items-center">
						<Tabs v-model="selectedTab">
							<TabsList>
								<TabsTrigger value="all">All</TabsTrigger>
								<TabsTrigger v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</TabsTrigger>
							</TabsList>
						</Tabs>
						<Input v-model="searchQuery" placeholder="Search widgets..." class="max-w-xs" />
					</div>

					<div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
						<Card v-for="widget in filteredWidgets" :key="widget.id" class="hover:shadow-md transition-shadow">
							<CardHeader>
								<div class="flex justify-between items-start">
									<div>
										<CardTitle class="text-base">{{ widget.name }}</CardTitle>
										<CardDescription>{{ widget.type }}</CardDescription>
									</div>
									<Badge :variant="widget.status === 'active' ? 'default' : 'secondary'">{{ widget.status }}</Badge>
								</div>
							</CardHeader>
							<CardContent>
								<p v-if="widget.description" class="text-sm text-muted-foreground mb-3">{{ widget.description }}</p>
								<div class="flex space-x-2">
									<Button variant="outline" size="sm" @click="showEmbed(widget)">
										<Code2 class="h-3 w-3 mr-1" />
										Embed
									</Button>
									<Button variant="outline" size="sm">
										<Eye class="h-3 w-3 mr-1" />
										Preview
									</Button>
									<DropdownMenu>
										<DropdownMenuTrigger as-child>
											<Button variant="ghost" size="sm">
												<EllipsisVerticalIcon class="h-4 w-4" />
											</Button>
										</DropdownMenuTrigger>
										<DropdownMenuContent>
											<DropdownMenuItem>Edit</DropdownMenuItem>
											<DropdownMenuItem>Duplicate</DropdownMenuItem>
											<DropdownMenuItem class="text-destructive" @click="handleDelete(widget)">Delete</DropdownMenuItem>
										</DropdownMenuContent>
									</DropdownMenu>
								</div>
							</CardContent>
						</Card>
						<Card v-if="filteredWidgets.length === 0" class="col-span-full">
							<CardContent class="h-32 flex items-center justify-center text-muted-foreground">
								<div v-if="loading">Loading widgets...</div>
								<div v-else>No widgets found. Create your first widget to get started.</div>
							</CardContent>
						</Card>
					</div>
				</div>
			</CardContent>
		</Card>

		<Dialog v-model:open="showEmbedDialog">
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Embed Code</DialogTitle>
					<DialogDescription>Copy this code to embed the widget on your site</DialogDescription>
				</DialogHeader>
				<div class="space-y-4 py-4">
					<Textarea v-model="embedCode" rows="4" class="font-mono text-sm" readonly />
				</div>
				<DialogFooter>
					<Button variant="outline" @click="showEmbedDialog = false">Close</Button>
					<Button @click="copyEmbed">Copy to Clipboard</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	</div>
</template>
